import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useMember } from "@/context/MemberContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import AuthShell, { fieldCls, primaryBtn } from "@/components/store/AuthShell";
import ResendVerification from "@/components/store/ResendVerification";

export default function StoreLogin() {
  const { member, login } = useMember();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [unverified, setUnverified] = useState(false);
  const [busy, setBusy] = useState(false);
  const next = params.get("next")?.startsWith("/") ? params.get("next") : "/shop";

  if (member) return <Navigate to={next} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setUnverified(false); setBusy(true);
    try {
      await login(email, password);
      nav(next, { replace: true });
    } catch (e2) {
      setUnverified(e2.response?.status === 403);
      setErr(formatApiErrorDetail(e2.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Member log in" subtitle="Log in to access the ATL catalogue." testid="store-login-page">
      {params.get("expired") && !err && <p className="mb-4 rounded-lg bg-sky-50 px-3 py-2 text-sm text-sky-800" data-testid="login-expired-notice">Your session has ended. Please log in again.</p>}
      {params.get("verified") && <p className="mb-4 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid="login-verified-notice">Email verified — you can now log in.</p>}
      {params.get("reset") && <p className="mb-4 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid="login-reset-notice">Password updated — log in with your new password.</p>}
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5"><Label htmlFor="l-email">Email address</Label><Input id="l-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={fieldCls} autoComplete="email" data-testid="login-email-input" /></div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between"><Label htmlFor="l-pw">Password</Label><Link to="/forgot-password" className="text-xs font-semibold text-sky-700 hover:text-sky-900" data-testid="login-forgot-link">Forgot password?</Link></div>
          <Input id="l-pw" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={fieldCls} autoComplete="current-password" data-testid="login-password-input" />
        </div>
        {err && <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="login-error">{err}</p>}
        <Button type="submit" disabled={busy} className={primaryBtn} data-testid="login-submit-button">{busy ? "Logging in…" : "Log in"}</Button>
      </form>
      {unverified && <ResendVerification email={email} />}
      <p className="mt-6 text-center text-sm text-slate-600">New to ATL? <Link to="/register" className="font-semibold text-sky-700 hover:text-sky-900" data-testid="login-register-link">Create an account</Link></p>
    </AuthShell>
  );
}
